import {Money} from '@dintero/money';

import type {CommerceItem} from '@/sanity/schemas/types';

import type {ItemForPriceFragment} from './query';

type ItemPriceFields = Pick<ItemForPriceFragment, 'priceExVat' | 'isRoundPriceIncVat'>;
type VatTypePriceFields = NonNullable<ItemForPriceFragment['resolvedVatType']>;

/**
 * Calculates the price of a quantity of the item, both including and excluding VAT.
 */
export function getItemQuantityPriceWithVat(
	item: ItemPriceFields,
	vatType: VatTypePriceFields,
	quantity: number,
) {
	const unitPrice = getItemUnitPriceWithVat(item, vatType);

	// NOTE: I'm a bit unsure about this calculation. Doing all calculations bases on unit prices
	// prices with the VAT innluded makes the most logical sense (at least to me), but it does
	// mean that the effective price ex.VAT per unit may be contain fractions of øre. As long as
	// we never have to display prices EX VAT this should not become a problem.
	//
	// I've seen different webshops and systems do this in different ways. Some muliply by the
	// quantity ex. VAT and then add the VAT on the sum, others do what we do here. Both ways have
	// trades offs in terms of where the rounding happens, so I guess we just have to pick the way
	// that makes the most sense to us.

	const quantityAmountIncVat = unitPrice.amountIncVat.multiply(quantity);
	const quantityAmountVat = quantityAmountIncVat.getVat(unitPrice.vatPercentage, true);

	return {
		vatPercentage: unitPrice.vatPercentage,
		vatCode: unitPrice.vatCode,
		quantity: quantity,
		unitAmountIncVat: unitPrice.amountIncVat,
		unitAmountVat: unitPrice.amountVat,
		amountIncVat: quantityAmountIncVat,
		amountVat: quantityAmountVat,
	};
}

/**
 * Calculates the price of a single unit of the item, both including and excluding VAT.
 */
export function getItemUnitPriceWithVat(item: ItemPriceFields, vatType: VatTypePriceFields) {
	// VAT percentage and code are required fields in Sanity, so if they are missing we are most
	// likely in preview mode. Default to some sensible values in that case to avoid crashing
	// completely.
	const vatPercentage = vatType.percentage ?? 25;
	const vatCode = vatType.code ?? '3';

	let amountExVat = Money.fromFractionlessAmount(item.priceExVat ?? 0, 'NOK');
	let amountIncVat = amountExVat.addVat(vatPercentage);
	if (item.isRoundPriceIncVat) {
		amountIncVat = amountIncVat.round(0);
		amountExVat = amountIncVat.removeVat(vatPercentage);
	}

	const amountVat = amountIncVat.getVat(vatPercentage);

	return {
		amountExVat: amountExVat,
		amountIncVat: amountIncVat,
		amountVat: amountVat,
		vatPercentage: vatPercentage,
		vatCode: vatCode,
	};
}

export type ShippingVolumeWeight = {
	volume: number;
	weight: number;
};

/**
 * Calculates the combined shipping-weight and volume estimate for a list of items.
 *
 * The volume defaults to 0dm3 if none has been set. Knowing the item volume is critical for
 * getting an accurate price.
 *
 * If weight is missing for one or more items, the function
 * will just default to 1 kg. This is based on a recommendation from Profrakt.
 */
export function getItemVolumeWeightForShipping(items: CommerceItem[]): ShippingVolumeWeight {
	let volume = 0;
	let weight = 0;
	let hasWeightForAllItems = true;

	// Stack all items on top of each other and sum up volume and weight:
	for (const item of items) {
		if (item.itemType !== 'physical') {
			// Non-physical items don't have dimensions or weight:
			continue;
		}

		if (item.volume) {
			volume += item.volume;
		}

		if (item.weight) {
			weight += item.weight;
		} else {
			hasWeightForAllItems = false;
		}
	}

	return {
		// Unless we know for certain that all item weights are known, default to a 1 kg package. This
		// is based on a reccommendation from Profrakt:
		volume,
		weight: hasWeightForAllItems && weight ? weight : 1,
	};
}
